import i18n from '@/i18n'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { formatMoney } from '@/utils/NumericMutations'
import { ActivityModifier, AddressType, ContractMode } from '@/utils/Enumerations'
import { activityPeriodRange } from '@/utils/pdf-formatters/pdfHelpers'
import { upperFirst } from 'lodash'

export const appendColon = string => `${string}:`

export const joinOrNotApplicable = items => items.join(',   ') || i18n.t('notApplicable')

export function joinWithConjunction (items) {
  if (typeof Intl.ListFormat !== 'function') return items.join(', ')
  return new Intl.ListFormat(i18n.locale, { style: 'long', type: 'conjunction' }).format(items)
}

export function billingAddress (account) {
  return (account.addresses ?? []).find(ad => ad.address.type === AddressType.BillTo.value)?.address
}

export function addressLines (address) {
  return [address.addressLine1, address.addressLine2, `${address.city}, ${address.state} ${address.postalCode}`].filter(Boolean)
}

export const divider = ({ lineWidth = 1, margin = [0, 19, 0, 19] } = {}) => ({
  canvas: [
    { type: 'line', x1: 0, y1: 0, x2: 760, y2: 0, lineWidth }
  ],
  margin
})

export function nameAddressBlock (name, address, { alignment = 'left', nameStyle = 'title' } = {}) {
  const line = (text, style) => ({ text, listType: 'none', style, alignment })
  return [
    line(name, nameStyle),
    ...(address ? addressLines(address).map(text => line(text, 'subtitle')) : [])
  ]
}

export function headerBlock ({ companyInfo }, { account, title } = {}) {
  const company = nameAddressBlock(companyInfo.name, companyInfo.address)
  const columns = account
    ? [company, nameAddressBlock(account.name, billingAddress(account), { alignment: 'right' })]
    : [company, { text: title ?? '', style: 'title', alignment: 'center' }, { text: '' }]

  return [
    { columns },
    divider()
  ]
}

const SINGLE_COLUMN_CONTRACT_INFO_MODES = [ContractMode.Logs.value]

const INFO_COLUMN_GAP = 10

export function infoBlock (params) {
  return {
    columns: params.tract ? [tractInfo(params), contractInfo(params)] : [contractInfo(params)],
    columnGap: INFO_COLUMN_GAP
  }
}

export function infoSection (title, ...tables) {
  return [
    { text: title, style: 'title', margin: [0, 0, 0, 8], alignment: 'left' },
    ...tables
  ]
}

function tractInfo ({ tract, landowners }) {
  const paired = labelValueTable({
    groups: [
      [
        { label: appendColon(i18n.t('tractName')), value: tract.name },
        { label: appendColon(i18n.t('tractType')), value: tract.type.name },
        { label: appendColon(i18n.t('forester')), value: tract.foresterUser?.name ?? i18n.t('notApplicable') },
        { label: appendColon(i18n.t('certifications')), value: joinOrNotApplicable((tract.tractCertifications ?? []).map(c => c.name)) }
      ],
      [
        { label: appendColon(i18n.t('county')), value: tract.spot.countrySecondarySubdivision },
        { label: appendColon(i18n.t('state')), value: tract.spot.countrySubdivision },
        { label: appendColon(i18n.t('latLong')), value: `${tract.spot.latitude}, ${tract.spot.longitude}` },
        { label: appendColon(i18n.t('permitNumbers')), value: joinOrNotApplicable((tract.permitNumbers ?? []).map(p => p.number)) }
      ]
    ]
  })

  const fullWidth = labelValueTable({
    groups: [[
      { label: appendColon(i18n.t('landowners')), value: joinOrNotApplicable(landowners.map(lo => `${lo.accountName} (${lo.ownership}%)`)) }
    ]]
  })

  return infoSection(i18n.t('tractProperties'), paired, fullWidth)
}

function contractInfo (params) {
  const groups = contractInfoGroups(params)
  const singleColumn = SINGLE_COLUMN_CONTRACT_INFO_MODES.includes(params.contractMode.value)
  return infoSection(i18n.t('contractInfo'), labelValueTable({ groups: singleColumn ? [groups.flat()] : groups }))
}

function contractInfoGroups ({ contract, tract, loggers, activityPeriod }) {
  const destination = (contract.distance > 0)
    ? `${contract.destination} (${contract.distance}mi.)`
    : contract.destination
  const contractPeriod = `${utcToLocalDate(contract.effectiveDate)} - ${utcToLocalDate(contract.expirationDate)}`

  const parties = []
  if (tract) {
    parties.push({ label: appendColon(i18n.t('setting')), value: contract.setting || i18n.t('notApplicable') })
  }
  if (contract.fromAccount) {
    parties.push({ label: appendColon(i18n.t('fromAccount')), value: contract.fromAccount })
  }
  parties.push({ label: appendColon(i18n.t('account')), value: contract.account || i18n.t('notApplicable') })
  parties.push({ label: appendColon(i18n.t('destination')), value: destination })
  if (tract) {
    parties.push({ label: appendColon(i18n.t('loggers')), value: joinOrNotApplicable(loggers.map(logger => logger.name)) })
  }

  const dates = [{ label: appendColon(i18n.t('contractPeriod')), value: contractPeriod }]
  if (activityPeriod) {
    const label = activityPeriod.timezone ? `${i18n.t('activityPeriod')} (${activityPeriod.timezone})` : i18n.t('activityPeriod')
    dates.push({ label: appendColon(label), value: activityPeriodRange(activityPeriod) })
  }

  return [parties, dates]
}

function labelValueTable ({ groups }) {
  const label = text => ({ text, style: 'header', noWrap: true })
  const value = text => ({ text, style: 'subtitle' })
  const rowCount = Math.max(...groups.map(rows => rows.length))
  const body = Array.from({ length: rowCount }, (_, index) => groups.flatMap(rows => {
    const row = rows[index]
    return row ? [label(row.label), value(row.value)] : [{ text: '' }, { text: '' }]
  }))

  return {
    table: {
      widths: groups.flatMap(() => ['auto', '*']),
      body
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: columnIndex => columnIndex > 0 && columnIndex % 2 === 0 ? 16 : 0,
      paddingRight: columnIndex => columnIndex % 2 === 0 ? 8 : 0,
      paddingTop: () => 0,
      paddingBottom: () => 4
    }
  }
}

const FILE_NAME_CODE_BY_MODE = {
  [ContractMode.Logs.value]: 'Logs',
  [ContractMode.Byproducts.value]: 'Bp',
  [ContractMode.Transfers.value]: 'Trn',
  [ContractMode.LogYardSale.value]: 'Lys'
}

export const contentTypeCode = contractMode => FILE_NAME_CODE_BY_MODE[contractMode.value] ?? upperFirst(contractMode.label)

export function distanceSuffix (activity, contract) {
  return activity.activityTemplate.modifier === ActivityModifier.DistanceTraveled.value && contract.distance !== 0
    ? ` (${contract.distance}mi.)`
    : ''
}

export const embeddedRateSheetName = cost => typeof cost.rateSheet === 'string' ? cost.rateSheet : cost.rateSheet?.name

function rateOrSheetName (cost, suffix = '') {
  if (!cost) return i18n.t('notApplicable')
  if (cost.rateSheetId) return embeddedRateSheetName(cost) ?? i18n.t('notApplicable')
  return formatMoney(cost.rate) + suffix
}

export const baseRateText = (activity, suffix) => rateOrSheetName(activity.baseCost, suffix)

export const detailRateText = detail => rateOrSheetName(detail.cost)

export const sortByActivityName = activities => [...activities].sort((a, b) => a.activityTemplate.name.localeCompare(b.activityTemplate.name))

export const sortByProductName = details => [...details].sort((a, b) => a.product.name.localeCompare(b.product.name))

export function getRateSheets (activities) {
  const sheets = new Map()
  for (const activity of activities) {
    const activityName = activity.activityTemplate.name
    const baseId = activity.baseCost?.rateSheetId
    if (baseId && !sheets.has(baseId)) {
      sheets.set(baseId, { rateSheetId: baseId, activityName })
    }
    for (const detail of activity.activityDetails) {
      const detailId = detail.cost?.rateSheetId
      if (detailId && !sheets.has(detailId)) {
        sheets.set(detailId, { rateSheetId: detailId, activityName: `${activityName} - ${detail.product.name}` })
      }
    }
  }
  return [...sheets.values()]
}

export const gridRowFillLayout = {
  fillColor: function (rowIndex) {
    return (rowIndex % 2 === 0) ? 'white' : 'lightgrey'
  },
  vLineWidth: function (i) {
    return 0
  },
  hLineWidth: function (i) {
    if (i === 1) {
      return 1
    } else {
      return 0
    }
  }
}

export const documentStyles = {
  title: {
    fontSize: 15,
    bold: true
  },
  subtitle: {
    fontSize: 8,
    bold: false
  },
  header: {
    fontSize: 8,
    bold: true
  },
  sectionTitle: {
    fontSize: 12,
    bold: true
  }
}
